import { useCallback, useEffect, useMemo, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import {
  Inbox, Loader2, ClipboardPaste, AlertTriangle,
  ListFilter, Check, X, ArrowUp, ArrowDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { preconApi } from "./lib/api";
import { ItbCardDrawer } from "./components/ItbCardDrawer";
import MonthPicker from "@/components/MonthPicker";

const TYPE_LABEL = {
  itb: "ITB", addendum: "Addendum", date_change: "Date change", inquiry: "Inquiry",
  award_notice: "Award", post_bid_question: "Post-bid", other: "Other",
};

// Top view = anything that still needs a human. Everything else lives in the Archive below.
const ACTIVE_STATUSES = ["new", "unmatched", "later"];
const ARCHIVE_FILTERS = [
  ["bid", "Bid"], ["merged", "Merged"], ["matched", "Filed"],
  ["declined", "Declined"], ["other", "Other"], ["archived", "Archived"],
];
const ARCHIVE_STATUSES = ARCHIVE_FILTERS.map(([k]) => k);

const STATUS_LABEL = {
  new: "Needs review", unmatched: "Unmatched", later: "Later", bid: "Bid", merged: "Merged",
  matched: "Filed", declined: "Declined", other: "Other", archived: "Archived",
};
const isQualified = (c) => c.status === "bid" || !!c.bid_job_id;

// [key, header, value accessor used for sorting]
const COLUMNS = [
  ["received", "Received", (c) => c.created_at || ""],
  ["project", "Name", (c) => (c.parsed || {}).project_name || c.raw_subject || ""],
  ["customers", "Customer(s)", (c) => (c.customers || []).join(", ")],
  ["bid_due", "Bid due", (c) => (c.parsed || {}).bid_due_date || ""],
  ["start", "Start", (c) => (c.parsed || {}).start_date || ""],
  ["completion", "Completion", (c) => (c.parsed || {}).completion_date || ""],
  ["scopes", "Scopes", (c) => (c.scopes_mapped || []).map((x) => x.scope_code).join(" ")],
  ["facility", "Facility", (c) => (c.parsed || {}).facility_type || ""],
  ["qualify", "Qualify", (c) => (isQualified(c) ? "Y" : "N")],
];

export function readHeaders(text) {
  const out = {};
  const from = /^\s*(?:from|sender)\s*:\s*(.+)$/im.exec(text || "");
  if (from) {
    const line = from[1].trim();
    const email = /[\w.+-]+@[\w-]+\.[\w.-]+/.exec(line);
    if (email) out.from_email = email[0];
    const name = line.replace(/<[^>]*>/g, "").replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "")
      .replace(/["']/g, "").replace(/[<>(),;]/g, " ").trim();
    if (name) out.from_name = name.replace(/\s{2,}/g, " ");
    else if (out.from_email) out.from_name = out.from_email.split("@")[0].replace(/[._]/g, " ");
  }
  const subject = /^\s*subject\s*:\s*(.+)$/im.exec(text || "");
  if (subject) out.subject = subject[1].replace(/^(?:re|fw|fwd)\s*:\s*/i, "").trim();
  return out;
}

function filterSortSearch(items, statuses, searchStr, sort) {
  const q = (searchStr || "").trim().toLowerCase();
  let out = items.filter((c) => statuses.includes(c.status));
  if (q) {
    out = out.filter((c) => {
      const p = c.parsed || {};
      const hay = [p.project_name, c.raw_subject, p.gc_name, (c.customers || []).join(" "),
        c.from_name, c.from_email, p.facility_type, p.city, p.state].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }
  const col = COLUMNS.find((c) => c[0] === sort.key);
  if (col) {
    const get = col[2];
    out = [...out].sort((a, b) => {
      const cmp = String(get(a) || "").localeCompare(String(get(b) || ""),
        undefined, { numeric: true, sensitivity: "base" });
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }
  return out;
}

const Th = ({ col, sort, onSort }) => (
  <th onClick={() => onSort(col[0])} data-testid={`itb-col-${col[0]}`} data-active={sort.key === col[0] ? "1" : "0"}
      className={`cursor-pointer select-none px-3 py-2 font-semibold ${["start", "completion"].includes(col[0]) ? "text-center" : "text-left"}`}>
    <span className="inline-flex items-center gap-1">
      {col[1]}
      {sort.key === col[0] && (sort.dir === "asc"
        ? <ArrowUp className="h-3 w-3" data-testid={`itb-col-${col[0]}-asc`} />
        : <ArrowDown className="h-3 w-3" data-testid={`itb-col-${col[0]}-desc`} />)}
    </span>
  </th>
);

const QualifyToggle = ({ card, canEdit, onBid }) => {
  const on = isQualified(card);
  const can = canEdit && !on && ["itb", "inquiry"].includes(card.type);
  return (
    <button type="button" disabled={!can} data-testid={`itb-qualify-${card.id}`} data-on={on ? "Y" : "N"}
            onClick={(e) => { e.stopPropagation(); if (can) onBid(card, e); }}
            title={on ? "Qualified — on the Bid Board" : `${STATUS_LABEL[card.status] || card.status} — set Y to push to the Bid Board`}
            className={`rounded px-2 py-0.5 text-[10px] font-semibold text-black ${on ? "bg-green-300" : "bg-red-500"} disabled:cursor-not-allowed`}>
      {on ? "Y" : "N"}
    </button>
  );
};

const Field = ({ label, value, onChange, testId }) => (
  <label className="block text-[11px] text-slate-500">
    {label}
    <Input value={value} onChange={(e) => onChange(e.target.value)}
           className="mt-0.5 h-8 text-xs" data-testid={testId} />
  </label>
);

const monthYear = (v) => (v ? new Date(`${v.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "");

// Start / Completion — parsed from the ITB, month + year like the Bid Board; blank = centred calendar icon.
const MonthCell = ({ card, field, canEdit, onEdited }) => {
  const v = (card.parsed || {})[field];
  const txt = <span className="whitespace-nowrap text-[11px] text-slate-600">{monthYear(v) || "—"}</span>;
  const save = async (val) => {
    try { await preconApi.editCard(card.id, { [field]: val }); onEdited?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not saved"); }
  };
  return (
    <td className="whitespace-nowrap px-3 py-2 text-center" onClick={(e) => e.stopPropagation()} data-testid={`itb-${field}-${card.id}`}>
      {canEdit ? <MonthPicker value={v} iconOnly={!v} testid={`itb-${field}-input-${card.id}`} onChange={save}>{txt}</MonthPicker> : txt}
    </td>
  );
};

function ItbTable({ rows, sort, onSort, onOpen, emptyText, testId,
                   showActions = false, canEdit = false, onBid, onDecline, onEdited }) {
  const colCount = COLUMNS.length + (showActions ? 1 : 0);
  return (
    <table className="w-full text-xs" data-testid={testId}>
      <thead className="bg-slate-50 text-[11px] text-slate-500">
        <tr>
          {COLUMNS.map((col) => <Th key={col[0]} col={col} sort={sort} onSort={onSort} />)}
          {showActions && <th className="px-3 py-2" />}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={colCount} className="py-10 text-center text-slate-400">
            {emptyText}
          </td></tr>
        )}
        {rows.map((card) => {
          const p = card.parsed || {};
          return (
            <tr key={card.id} onClick={() => onOpen(card.id)}
                className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                data-testid={`itb-row-${card.id}`}>
              <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                {(card.created_at || "").slice(0, 10)}
                {card.sla_breach && (
                  <span className="ml-1 rounded bg-red-100 px-1 text-[10px] text-red-700"
                        data-testid={`itb-sla-${card.id}`}>{card.sla_days}d</span>
                )}
              </td>
              <td className="max-w-[16rem] truncate px-3 py-2 font-medium text-slate-800">
                {p.project_name || card.raw_subject}
              </td>
              <td className="max-w-[11rem] truncate px-3 py-2 text-slate-700"
                  title={(card.customers || []).join(", ")}>
                {(card.customers || []).length
                  ? (card.customers.length > 1
                    ? `${card.customers[0]} +${card.customers.length - 1}`
                    : card.customers[0])
                  : "—"}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-slate-600">{p.bid_due_date || "—"}</td>
              <MonthCell card={card} field="start_date" canEdit={canEdit} onEdited={onEdited} />
              <MonthCell card={card} field="completion_date" canEdit={canEdit} onEdited={onEdited} />
              <td className="max-w-[9rem] truncate px-3 py-2 text-slate-500">
                {(card.scopes_mapped || []).map((x) => x.scope_code).join(" ") || "—"}
              </td>
              <td className="max-w-[9rem] truncate px-3 py-2">
                {p.facility_type
                  ? <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] text-indigo-700">
                      {p.facility_type}
                    </span>
                  : <span className="text-slate-300">—</span>}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <QualifyToggle card={card} canEdit={canEdit} onBid={onBid} />
                {card.needs_same_job_answer && (
                  <span className="ml-1 text-[10px] text-amber-700"
                        data-testid={`itb-samejob-${card.id}`}>same job?</span>
                )}
                {card.needs_reclassify && (
                  <span className="ml-1 text-[10px] text-amber-700"
                        data-testid={`itb-lowconf-${card.id}`}>check</span>
                )}
              </td>
              {showActions && (
                <td className="whitespace-nowrap px-3 py-2 text-right"
                    onClick={(e) => e.stopPropagation()}>
                  {canEdit && (
                    <div className="flex items-center justify-end gap-1">
                      {["itb", "inquiry"].includes(card.type) && !card.bid_job_id && (
                        <button onClick={(e) => onBid(card, e)}
                                title="Push to the Bid Board (Qualify) — carries all the details over"
                                data-testid={`itb-quickbid-${card.id}`}
                                className="rounded p-1 text-blue-600 transition-colors hover:bg-blue-50">
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={(e) => onDecline(card, e)}
                              title="No bid — decline and send to the archive"
                              data-testid={`itb-quickdecline-${card.id}`}
                              className="rounded p-1 text-rose-600 transition-colors hover:bg-rose-50">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function ItbInboxPage() {
  const { access } = useAuth();
  const canEdit = (access?.estimating || "none") === "write";
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "received", dir: "desc" });
  // The open card is a URL entry (?card=<id>) so Back closes it and Forward reopens it.
  const [cardParam, setCardParam] = useUrlState("card", "");
  const [open, setOpenCard] = useState(null);
  const setOpen = (card) => { if (card) setOpenCard(card); setCardParam(card ? card.id : ""); };
  useEffect(() => {
    if (!cardParam) { setOpenCard(null); return; }
    if (open?.id === cardParam) return;
    preconApi.getCard(cardParam).then(setOpenCard).catch(() => setCardParam(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardParam]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [paste, setPaste] = useState({ subject: "", body: "", from_name: "", from_email: "" });
  const [pasteStep, setPasteStep] = useState("paste");
  const [pendingCard, setPendingCard] = useState(null);
  const [manual, setManual] = useState({ gc_name: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [busy, setBusy] = useState(false);
  // Archive section — collapsed by default, with its own search and a status multi-select.
  const [view, setView] = useUrlState("view", "inbox");
  const archiveOpen = view === "archive";
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveStatuses, setArchiveStatuses] = useState(ARCHIVE_STATUSES);
  const [archiveSort, setArchiveSort] = useState({ key: "received", dir: "desc" });

  const load = useCallback(() => preconApi.listCards({ include_archived: true, limit: 1000 })
    .then(setData)
    .catch(() => toast.error("Could not load the ITB inbox")), []);

  // Forwarded ITBs arrive by webhook; the list refreshes itself instead of a Refresh button.
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const toggleSort = (setter) => (key) =>
    setter((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "received" ? "desc" : "asc" });

  const toggleArchiveStatus = (key) =>
    setArchiveStatuses((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);

  const openCard = async (id) => {
    try { setOpen(await preconApi.getCard(id)); }
    catch { toast.error("Could not open that card"); }
  };

  const refreshOpen = async () => {
    if (open) setOpen(await preconApi.getCard(open.id));
    load();
  };

  const quickBid = async (card, e) => {
    e.stopPropagation();
    try {
      const out = await preconApi.bid(card.id);
      if (out.action === "review")
        toast.warning("Looks like a job already on the board — open the card to confirm the match");
      else if (out.action === "attached")
        toast.success("Added as another GC invitation on the existing job");
      else toast.success("Pushed to the Bid Board (Qualify)");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not push to the board");
    }
  };

  const quickDecline = async (card, e) => {
    e.stopPropagation();
    try {
      await preconApi.decline(card.id,
        { reason: "other", note: "No bid (quick action from inbox)", declined_in_portal: false });
      toast.success("Declined — moved to the archive");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not decline that card");
    }
  };

  const submitPaste = async () => {
    setBusy(true);
    try {
      const hdr = readHeaders(paste.body);
      const card = await preconApi.intakeManual({
        body: paste.body,
        subject: paste.subject || hdr.subject || "",
        from_name: paste.from_name || hdr.from_name || "",
        from_email: paste.from_email || hdr.from_email || "",
      });
      if (card.duplicate_of_existing) {
        toast.success("That email is already in the inbox — attached to the existing card");
        closePaste(); load(); return;
      }
      const p = card.parsed || {};
      const company = (p.gc_name || "").trim();
      const contact = (p.contact_name || "").trim() || (p.contact_email || "").trim();
      if (company && contact) {
        toast.success(`Read ${company}${p.contact_name ? " · " + p.contact_name : ""}`);
        closePaste(); load(); openCard(card.id); return;
      }
      // Couldn't read the sender cleanly — ask the user to fill only what's missing.
      setPendingCard(card);
      setManual({ gc_name: p.gc_name || "", contact_name: p.contact_name || "",
                  contact_email: p.contact_email || "", contact_phone: p.contact_phone || "" });
      setPasteStep("confirm");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that email");
    } finally {
      setBusy(false);
    }
  };

  const saveManual = async () => {
    setBusy(true);
    try {
      await preconApi.editCard(pendingCard.id, manual);
      toast.success("Sender saved");
      const id = pendingCard.id;
      closePaste(); load(); openCard(id);
    } catch {
      toast.error("Could not save the sender details");
    } finally {
      setBusy(false);
    }
  };

  const closePaste = () => {
    setPasteOpen(false);
    setPasteStep("paste");
    setPendingCard(null);
    setPaste({ subject: "", body: "", from_name: "", from_email: "" });
    setManual({ gc_name: "", contact_name: "", contact_email: "", contact_phone: "" });
  };

  const items = useMemo(() => data?.items || [], [data]);
  const activeRows = useMemo(
    () => filterSortSearch(items, ACTIVE_STATUSES, search, sort),
    [items, search, sort]);
  const archiveRows = useMemo(
    () => filterSortSearch(items, archiveStatuses, archiveSearch, archiveSort),
    [items, archiveStatuses, archiveSearch, archiveSort]);
  const archiveTotal = useMemo(
    () => items.filter((c) => ARCHIVE_STATUSES.includes(c.status)).length, [items]);

  if (!data) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading the ITB inbox…
      </div>
    );
  }

  const s = data.settings;
  return (
    <div className="page-body" data-testid="itb-inbox-page">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          {archiveOpen
            ? <SearchBar value={archiveSearch} onChange={setArchiveSearch} placeholder="Search..." testId="itb-archive-search" />
            : <SearchBar value={search} onChange={setSearch} placeholder="Search..." testId="itb-search" />}
          <Segmented
            options={[{ key: "inbox", label: "Inbox", testId: "itb-view-inbox" },
                      { key: "archive", label: "Archive", count: archiveTotal || undefined, testId: "itb-view-archive" }]}
            value={view} onChange={setView} />
          {archiveOpen && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="nav" className="h-7 text-xs" data-testid="itb-archive-status-filter">
                  <ListFilter className="mr-1 h-3.5 w-3.5" />
                  {archiveStatuses.length === ARCHIVE_STATUSES.length ? "All statuses" : `${archiveStatuses.length} statuses`}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                <DropdownMenuLabel className="text-[11px]">Show statuses</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {ARCHIVE_FILTERS.map(([key, label]) => (
                  <DropdownMenuCheckboxItem key={key} checked={archiveStatuses.includes(key)}
                    onCheckedChange={() => toggleArchiveStatus(key)} onSelect={(e) => e.preventDefault()}
                    data-testid={`itb-archive-status-${key}`}>
                    {label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canEdit && (
            <Button size="sm" className="h-7 px-2.5 text-xs bg-[#007BFF] hover:bg-[#0069d9] text-white" onClick={() => setPasteOpen(true)}
                    title="Paste an ITB email" aria-label="Paste ITB" data-testid="itb-paste-btn">
              <ClipboardPaste className="mr-1 h-3.5 w-3.5" /> ITB
            </Button>
          )}
          {!s.intake_enabled && (
            <span className="rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid="itb-intake-paused">
              Intake paused — turn it on in Settings › Inbox
            </span>)}
          <div className="ml-auto flex items-center gap-3">
            {data.sla_breaches > 0 && (
              <span className="flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-[11px] text-red-700"
                    data-testid="itb-sla-count">
                <AlertTriangle className="h-3 w-3" /> {data.sla_breaches} past the intake SLA
              </span>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {archiveOpen ? (
            <ItbTable rows={archiveRows} sort={archiveSort} onSort={toggleSort(setArchiveSort)}
                      onOpen={openCard} testId="itb-archive-table" canEdit={canEdit} onBid={quickBid} onEdited={load}
                      emptyText="Nothing archived matches." />
          ) : (
            <ItbTable rows={activeRows} sort={sort} onSort={toggleSort(setSort)} onOpen={openCard}
                      testId="itb-table" showActions canEdit={canEdit}
                      onBid={quickBid} onDecline={quickDecline} onEdited={load}
                      emptyText="Nothing needs a look. New invitations and anything unmatched show here." />
          )}
        </div>

        <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-400">
          <Inbox className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Not connected yet: the mailbox subscription, BuildingConnected pulls, and
          auto-decline suggestions. Everything above works on forwarded email today — an invitation
          forwarded to {s.forward_address} lands here classified and parsed.
          {!s.auto_attach_enabled
            && " Auto-attach is off, so every possible same-job match is asked, never assumed."}
        </p>
      </div>

      {open && (
        <ItbCardDrawer card={open} onClose={() => { setOpen(null); load(); }}
                       onChanged={refreshOpen} />
      )}

      <Dialog open={pasteOpen} onOpenChange={(v) => (v ? setPasteOpen(true) : closePaste())}>
        <DialogContent className="max-w-2xl" data-testid="itb-paste-dialog">
          <DialogHeader>
            <DialogTitle>{pasteStep === "paste" ? "Paste an invitation" : "Confirm the sender"}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">
            Paste a forwarded invitation; it is classified and read automatically.
          </DialogDescription>

          {pasteStep === "paste" ? (
            <div className="space-y-2">
              <textarea rows={16} value={paste.body}
                        placeholder="Paste the whole email here — including the signature. We read the sender, company, project, bid date and scopes for you."
                        onPaste={(e) => {
                          const text = e.clipboardData?.getData("text") || "";
                          const found = readHeaders(text);
                          if (Object.keys(found).length)
                            setPaste((p) => ({ ...p, ...found, body: text }));
                        }}
                        onChange={(e) => setPaste({ ...paste, body: e.target.value })}
                        className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs leading-relaxed focus:border-[#007BFF] focus:outline-none"
                        data-testid="itb-paste-body" />
              <p className="text-[10px] text-slate-400">
                One paste is enough. We only ask you to type the sender in by hand if we can't find
                it in the email. Nothing is emailed out.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500" data-testid="itb-manual-prompt">
                Read as <span className="font-medium text-slate-700">
                  {TYPE_LABEL[pendingCard?.type] || pendingCard?.type}</span>
                {pendingCard?.parsed?.project_name && (
                  <> for <span className="font-medium text-slate-700">
                    {pendingCard.parsed.project_name}</span></>)}.
                We couldn't read the sender cleanly — add what's missing:
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Company (GC)" testId="itb-manual-company" value={manual.gc_name}
                       onChange={(v) => setManual({ ...manual, gc_name: v })} />
                <Field label="Contact name" testId="itb-manual-contact" value={manual.contact_name}
                       onChange={(v) => setManual({ ...manual, contact_name: v })} />
                <Field label="Contact email" testId="itb-manual-email" value={manual.contact_email}
                       onChange={(v) => setManual({ ...manual, contact_email: v })} />
                <Field label="Contact phone" testId="itb-manual-phone" value={manual.contact_phone}
                       onChange={(v) => setManual({ ...manual, contact_phone: v })} />
              </div>
            </div>
          )}

          <DialogFooter>
            {pasteStep === "paste" ? (
              <>
                <Button variant="outline" onClick={closePaste}
                        data-testid="itb-paste-cancel">Cancel</Button>
                <Button onClick={submitPaste} disabled={busy || !paste.body.trim()}
                        data-testid="itb-paste-submit">
                  {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />} Read it
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline"
                        onClick={() => { const id = pendingCard.id; closePaste(); openCard(id); }}
                        data-testid="itb-manual-skip">Skip for now</Button>
                <Button onClick={saveManual} disabled={busy} data-testid="itb-manual-save">
                  {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />} Save sender
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
